import { useMemo, useRef, useState } from 'react';

import { useClickOutside } from '@hooks/index';
import { Popover } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { fr } from 'date-fns/locale';
import { Chevron, ChevronProps, DayPicker } from 'react-day-picker';
import { IconCalendar } from '@tabler/icons-react';
import classNames from 'classnames';

type DateTimePickerProps = {
	value: Dayjs | null;
	onChange: (value: Dayjs) => void;
	minuteStep?: number;
	className?: string;
};

const CustomChevron = (props: ChevronProps) => (
	<div className="rounded-md border border-slate-300 p-1">
		<Chevron {...props} className="fill-slate-300 text-slate-300" size={18} />
	</div>
);

const DateTimePicker = ({ value, onChange, minuteStep = 5, className }: DateTimePickerProps) => {
	const [open, setOpen] = useState(false);
	const { ref } = useClickOutside(open, setOpen);

	const defaultRef = useRef(
		dayjs()
			.add(1, 'hour')
			.minute(Math.ceil(dayjs().minute() / minuteStep) * minuteStep)
			.second(0)
	);

	const currentValue = value ?? defaultRef.current;

	const handleDateSelect = (date: Date | undefined) => {
		if (!date) return;
		onChange(dayjs(date).hour(currentValue.hour()).minute(currentValue.minute()).second(0));
	};

	const handleHourChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		onChange(currentValue.hour(Number(e.target.value)));
	};

	const handleMinuteChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		onChange(currentValue.minute(Number(e.target.value)));
	};

	const minutes = useMemo(
		() => Array.from({ length: Math.ceil(60 / minuteStep) }, (_, i) => i * minuteStep),
		[minuteStep]
	);

	return (
		<Popover
			trigger="click"
			open={open}
			content={
				<div ref={ref} className="flex flex-col gap-3">
					<DayPicker
						mode="single"
						selected={currentValue.toDate()}
						defaultMonth={currentValue.toDate()}
						onSelect={handleDateSelect}
						locale={fr}
						classNames={{
							day: 'h-1',
							selected: 'text-base font-bold bg-blue-600 text-blue-50 rounded-lg'
						}}
						navLayout="around"
						pagedNavigation
						required
						components={{
							Chevron: CustomChevron,
							CaptionLabel: (props) => (
								<span {...props} className={classNames(props.className, 'font-bold select-none')} />
							)
						}}
					/>
					<div className="border-t border-slate-200 pt-3">
						<div className="flex items-center justify-center gap-2">
							<div className="flex flex-1 flex-col items-center gap-1">
								<select
									value={currentValue.hour()}
									onChange={handleHourChange}
									className="w-full appearance-none rounded-md border border-slate-200 bg-white px-2 py-1.5 text-center text-sm hover:border-slate-300 focus:border-blue-500 focus:outline-none"
								>
									{Array.from({ length: 24 }, (_, i) => (
										<option key={i} value={i}>
											{String(i).padStart(2, '0')}
										</option>
									))}
								</select>
								<span className="text-xs text-slate-400">Heure</span>
							</div>
							<span className="text-lg font-bold text-slate-400">:</span>
							<div className="flex flex-1 flex-col items-center gap-1">
								<select
									value={currentValue.minute()}
									onChange={handleMinuteChange}
									className="w-full appearance-none rounded-md border border-slate-200 bg-white px-2 py-1.5 text-center text-sm hover:border-slate-300 focus:border-blue-500 focus:outline-none"
								>
									{minutes.map((m) => (
										<option key={m} value={m}>
											{String(m).padStart(2, '0')}
										</option>
									))}
								</select>
								<span className="text-xs text-slate-400">Minute</span>
							</div>
						</div>
					</div>
					<button
						type="button"
						onClick={() => setOpen(false)}
						className="w-full rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none"
					>
						Valider
					</button>
				</div>
			}
		>
			<button
				type="button"
				onClick={() => setOpen(true)}
				className={classNames(
					'flex items-center gap-2 rounded border border-solid border-slate-300 bg-white px-2 py-1 text-sm text-slate-700',
					className
				)}
			>
				<IconCalendar size={16} className="text-slate-400" />
				<span>{value ? value.format('DD MMM YYYY, HH:mm') : 'Choisir une date'}</span>
			</button>
		</Popover>
	);
};

export default DateTimePicker;
